import {
  applyDisplayDatePlaceholders,
  formatAsOfDdMmYyyy,
  resolveFactsheetDownloadAsOf,
} from '../../../../../utils/documentFileName';
import { isSquadDynamicCell, type SquadDynamicCell } from './squadFondsdatenDynamic';
import { SECTION_HEADER_DEFAULTS } from './templateConstants';

export type SquadSectionHeaderKey = keyof typeof SECTION_HEADER_DEFAULTS;
export type SquadSectionHeaderValue = string | SquadDynamicCell;
export type SquadSectionHeaders = Record<SquadSectionHeaderKey, SquadSectionHeaderValue>;

/** Section titles that support CellConfig (text tags + table.field). */
export const DYNAMIC_SECTION_HEADER_KEYS = new Set<SquadSectionHeaderKey>(['fondsdaten']);

const DATED_HEADER_PREFIX: Record<string, string> = {
  fondsdaten: 'Fondsdaten',
};

/**
 * Headers that carried „per <Datum>“ and no longer do (09.10.2026): the date moved into
 * the page header („Werbemitteilung (Stand: …)“). Stored dated titles lose the date on read.
 */
const UNDATED_HEADER_KEYS = new Set<SquadSectionHeaderKey>(['wertentwicklungRisiko']);

/** „… per 31.05.2026“ / „… per {date}“ / CellConfig text + cixon_chart.datum → title without the date. */
export function stripSquadHeaderDate(value: unknown): string {
  if (isSquadDynamicCell(value)) {
    // In field order: a date field takes the „per“ before it along, text after it stays.
    let text = '';
    for (const f of value.fields || []) {
      if (f.type === 'text') text += f.text || '';
      else text = text.replace(/\s+per\s*$/i, ' ');
    }
    return text.replace(/\s{2,}/g, ' ').trim();
  }
  return String(value ?? '')
    .replace(/\s+per\s+(\{date\}|\d{1,2}\.\d{1,2}\.\d{4})/i, '')
    .replace(/\{date\}/gi, '')
    .trim();
}

/**
 * Text for the page header next to the page counter: „Werbemitteilung (Stand: 30.09.2026)“.
 * „Stand“ is the factsheet date (`published_at`), the date the editor sets — not the newest
 * data row, which can lie a day earlier when the factsheet date is no trading day.
 */
export function squadPageMetaLabel(pdf?: Record<string, any> | null): string {
  const raw = pdf?.published_at;
  const valid =
    raw instanceof Date
      ? !Number.isNaN(raw.getTime())
      : /^(\d{4}-\d{2}-\d{2}|\d{1,2}\.\d{1,2}\.\d{4})/.test(String(raw ?? '').trim());
  // No valid date, no „Stand“ — the formatter would fall back to today.
  if (!valid) return 'Werbemitteilung';
  return `Werbemitteilung (Stand: ${formatAsOfDdMmYyyy(raw)})`;
}

/** Default CellConfig: static label + live `cixon_chart.datum`. */
export function makeSquadDatedSectionHeader(labelPrefix: string): SquadDynamicCell {
  const prefix = String(labelPrefix || '').trim().replace(/\s+per\s*$/i, '');
  return {
    isDynamic: true,
    fields: [
      { type: 'text', text: `${prefix} per ` },
      {
        type: 'table',
        table: 'cixon_chart',
        field: 'datum',
        valueType: 'date',
      },
    ],
  };
}

export function defaultDynamicSectionHeader(key: SquadSectionHeaderKey): SquadDynamicCell {
  return makeSquadDatedSectionHeader(DATED_HEADER_PREFIX[key] || String(SECTION_HEADER_DEFAULTS[key] || key));
}

/** Turn a string template (`… per {date}` / `… per 31.05.2026`) into CellConfig tags. */
export function stringToDatedSectionHeaderConfig(text: string): SquadDynamicCell {
  const raw = String(text || '').trim();
  if (!raw) return makeSquadDatedSectionHeader('Titel');
  const withPlaceholder = raw
    .replace(/(\sper\s+)\d{1,2}\.\d{1,2}\.\d{4}\b/i, '$1{date}')
    .replace(/\{DATE\}/g, '{date}');
  const idx = withPlaceholder.indexOf('{date}');
  if (idx >= 0) {
    const before = withPlaceholder.slice(0, idx);
    const after = withPlaceholder.slice(idx + '{date}'.length);
    const fields: SquadDynamicCell['fields'] = [
      { type: 'text', text: before },
      {
        type: 'table',
        table: 'cixon_chart',
        field: 'datum',
        valueType: 'date',
      },
    ];
    if (after) fields.push({ type: 'text', text: after });
    return { isDynamic: true, fields };
  }
  return makeSquadDatedSectionHeader(raw);
}

function shouldUpgradeStringToDynamic(key: SquadSectionHeaderKey, raw: string): boolean {
  if (!DYNAMIC_SECTION_HEADER_KEYS.has(key)) return false;
  if (!raw) return true;
  if (/\{date\}/i.test(raw)) return true;
  if (/\sper\s+\d{1,2}\.\d{1,2}\.\d{4}\b/i.test(raw)) return true;
  if (raw === SECTION_HEADER_DEFAULTS[key]) return true;
  return false;
}

export function normalizeSquadSectionHeaderValue(
  key: SquadSectionHeaderKey,
  value: unknown,
): SquadSectionHeaderValue {
  if (UNDATED_HEADER_KEYS.has(key)) return stripSquadHeaderDate(value) || SECTION_HEADER_DEFAULTS[key];
  if (isSquadDynamicCell(value)) return value;
  const raw = value == null ? '' : String(value);
  if (shouldUpgradeStringToDynamic(key, raw)) {
    return stringToDatedSectionHeaderConfig(raw || SECTION_HEADER_DEFAULTS[key]);
  }
  return raw || SECTION_HEADER_DEFAULTS[key];
}

/** Format a chart/API date string to DD.MM.YYYY when possible. */
export function formatSquadHeaderDatum(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';
  // Already German display
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(s)) return s;
  return formatAsOfDdMmYyyy(s);
}

/**
 * Resolve one header to display text.
 * Dynamic cells prefer the fetched GET_AUTOMATED_COLUMNS value; otherwise fall back
 * to concatenating text tags + as-of for datum fields (keeps editor usable offline).
 */
export function resolveSquadSectionHeaderText(
  value: SquadSectionHeaderValue | undefined | null,
  asOf?: string | Date | null,
  fetched?: string | null,
): string {
  if (isSquadDynamicCell(value)) {
    if (fetched != null && String(fetched).trim() !== '') {
      return formatSquadHeaderDatumParts(String(fetched).trim());
    }
    return (value.fields || [])
      .map((field) => {
        if (field.type === 'text') return field.text || '';
        if (field.type === 'table' && (field.field === 'datum' || field.valueType === 'date')) {
          return formatAsOfDdMmYyyy(asOf);
        }
        return '';
      })
      .join('');
  }
  const raw = value == null ? '' : String(value);
  return applyDisplayDatePlaceholders(raw, asOf);
}

/** If the API returns a full header with an ISO date at the end, normalize the date part. */
function formatSquadHeaderDatumParts(text: string): string {
  return text.replace(/(\sper\s+)(\d{4}-\d{2}-\d{2})\b/gi, (_, prefix, iso) => {
    return `${prefix}${formatAsOfDdMmYyyy(iso)}`;
  }).replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (iso) => formatAsOfDdMmYyyy(iso));
}

export function resolveSquadHeaderAsOf(pdf?: Record<string, any> | null): string | Date | undefined {
  return resolveFactsheetDownloadAsOf(pdf);
}

/** Merge defaults + saved headers; upgrade date titles to CellConfig when appropriate. */
export function mergeSquadSectionHeaders(
  saved?: Partial<Record<string, unknown>> | null,
): SquadSectionHeaders {
  const out = {} as SquadSectionHeaders;
  (Object.keys(SECTION_HEADER_DEFAULTS) as SquadSectionHeaderKey[]).forEach((key) => {
    out[key] = normalizeSquadSectionHeaderValue(key, saved?.[key] ?? SECTION_HEADER_DEFAULTS[key]);
  });
  if (saved) {
    Object.keys(saved).forEach((key) => {
      if (!(key in SECTION_HEADER_DEFAULTS)) return;
      out[key as SquadSectionHeaderKey] = normalizeSquadSectionHeaderValue(
        key as SquadSectionHeaderKey,
        saved[key],
      );
    });
  }
  return out;
}

/** Expand all headers to plain strings for preview/PDF navy bars. */
export function expandSquadSectionHeaders(
  headers: Partial<Record<string, unknown>> | null | undefined,
  asOf?: string | Date | null,
  fetchedByKey?: Map<string, string> | Record<string, string> | null,
): Record<SquadSectionHeaderKey, string> {
  const merged = mergeSquadSectionHeaders(headers);
  const getFetched = (key: string): string | undefined => {
    if (!fetchedByKey) return undefined;
    if (fetchedByKey instanceof Map) return fetchedByKey.get(key);
    return fetchedByKey[key];
  };
  const out = {} as Record<SquadSectionHeaderKey, string>;
  (Object.keys(merged) as SquadSectionHeaderKey[]).forEach((key) => {
    out[key] = resolveSquadSectionHeaderText(merged[key], asOf, getFetched(key));
  });
  return out;
}

export function isDynamicSectionHeaderKey(key: string): key is SquadSectionHeaderKey {
  return DYNAMIC_SECTION_HEADER_KEYS.has(key as SquadSectionHeaderKey);
}
